import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Drawer } from '@mui/material';
import { styled } from '@mui/system';
import { networkConfig } from 'config';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { authActions } from 'store/auth';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useAccount, useBalance, useDisconnect } from 'wagmi';
import { useEthersSigner } from 'hooks/useEtherSigner';
import { getErc20Contract } from 'utils/contractHelpers';
import { formatUnits } from 'ethers/lib/utils';
import MenuIcon from '@mui/icons-material/Menu';
import { MOBILE_BREAK_POINTS, SIDEBAR_WIDTH } from 'data/constant';
import { ConnectPanel } from 'components/Navbar/ConnectPanel';
import { DisconnectPanel } from 'components/Navbar/DisconnectPanel';

const DesktopOnly = styled(Box)({
	display: 'none',
	[`@media (min-width:${MOBILE_BREAK_POINTS}px)`]: {
		display: 'flex',
		height: '100vh',
		backgroundColor: '#fff',
		borderLeft: '1px solid rgba(0,0,0,0.1)',
		boxShadow: '0 0 24px rgba(0,0,0,0.06)',
		zIndex: 1200,
		flexDirection: 'column',
	},
});

const MobileOnly = styled('div')({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	[`@media (min-width:${MOBILE_BREAK_POINTS}px)`]: {
		display: 'none',
	},
});

const LaunchButton = styled(Button)(() => ({
	backgroundColor: 'transparent',
	width: '100%',
	whiteSpace: 'nowrap',
	color: '#000',
	padding: '12px 10px',
	fontFamily: 'Inter',
	fontWeight: 600,
	fontSize: '14px',
	letterSpacing: '0.15em',
	textTransform: 'uppercase',
	boxShadow: 'none',
	clipPath:
		'polygon(15px 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%, 0 15px)',
	'&:hover': {
		boxShadow: 'none',
	},
}));

const PanelContent: React.FC<{
	address?: `0x${string}` | undefined;
	onOpenWallet: () => void;
	onDisconnect: () => void;
}> = ({ address, onOpenWallet, onDisconnect }) => {
	return (
		<Box sx={{ width: 300 }}>
			{!address && <ConnectPanel onOpenWallet={onOpenWallet} />}
			{address && <DisconnectPanel onDisconnect={onDisconnect} />}
		</Box>
	);
};

export const SidebarButton: React.ComponentType<{ isLarge?: boolean }> = ({
	isLarge,
}) => {
	const appState = useSelector((state: RootState) => state.auth);
	const dispatch = useDispatch();
	const [balance, setBalance] = useState('0');
	const selectedVault = useSelector((state: RootState) => state.vault);
	const selectedAsset = selectedVault.token.find(
		(d) => d.name === selectedVault.selectedToken,
	);
	const { openConnectModal } = useConnectModal();
	const { address } = useAccount();
	const { data } = useBalance({ address });
	const { disconnect } = useDisconnect();
	const signer: any = useEthersSigner();
	const mainNet = Object.values(networkConfig);

	const bal = selectedAsset?.isNative
		? parseFloat(
				parseFloat(data?.formatted || '0').toFixed(2),
		  ).toLocaleString()
		: parseFloat(parseFloat(balance).toFixed(2)).toLocaleString();

	const toggleDrawer = (Open: boolean) => (event?: any) => {
		if (
			event?.type === 'keydown' &&
			(event.key === 'Tab' || event.key === 'Shift')
		)
			return;
		dispatch(authActions.openDrawer(Open));
	};

	const fetchBalance = useCallback(async () => {
		try {
			if (address) {
				const assetContract = getErc20Contract(
					selectedAsset?.address || '',
					signer?.provider.getSigner(address),
				);
				const assetBalance = await assetContract.balanceOf(address);
				setBalance(
					formatUnits(assetBalance, selectedAsset?.decimalValue),
				);
			} else {
				setBalance('0');
			}
		} catch {
			// swallow
		}
	}, [address, signer, selectedAsset?.address, selectedAsset?.decimalValue]);

	useEffect(() => {
		fetchBalance();
	}, [fetchBalance]);

	const panel = (
		<PanelContent
			address={address}
			onOpenWallet={() => dispatch(authActions.openWallet(true))}
			onDisconnect={() => disconnect()}
		/>
	);

	return (
		<>
			<DesktopOnly>{panel}</DesktopOnly>
			<MobileOnly
				style={{
					width: isLarge ? '100%' : 'initial',
				}}
			>
				<LaunchButton onClick={toggleDrawer(true)}>
					<MenuIcon />
				</LaunchButton>

				<Drawer
					anchor="right"
					open={appState.isDrawerOpen}
					onClose={toggleDrawer(false)}
					PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}
					style={{ zIndex: 4000 }}
				>
					<Box sx={{ height: '100%', overflowY: 'auto' }}>
						{panel}
					</Box>
				</Drawer>
			</MobileOnly>
		</>
	);
};
